import { useEffect, useState } from "react";

const PROVIDER_HINTS = {
  anthropic:
    "Visit console.anthropic.com → Log in → API Keys → Create Key. New accounts get free credits.",
  openai:
    "Visit platform.openai.com → Log in → API Keys → Create Key. Requires adding a payment method.",
  gemini:
    "Visit aistudio.google.com → Click Get API Key. Free tier available with a Google account.",
  xai: "Visit console.x.ai → API Keys → Create Key.",
  groq: "Visit console.groq.com → API Keys → Create Key. Free tier available.",
  azure:
    "Requires an Azure account. Visit portal.azure.com → Create an OpenAI resource → Keys and Endpoints.",
};

const SettingsModal = ({
  isOpen,
  providers,
  draftSettings,
  onDraftSettingsChange,
  onClose,
  onSave,
  onExport,
  onImport,
  fileInputRef,
}) => {
  const [isHintOpen, setIsHintOpen] = useState(false);

  useEffect(() => {
    setIsHintOpen(false);
  }, [draftSettings.activeProvider]);

  if (!isOpen) return null;

  const activeProviderLabel =
    providers.find((provider) => provider.id === draftSettings.activeProvider)
      ?.label || "Provider";

  const hint = PROVIDER_HINTS[draftSettings.activeProvider];

  return (
    <div
      className="pcm-modal-overlay"
      data-testid="settings-modal-overlay"
      onClick={onClose}
    >
      <div
        className="pcm-modal"
        data-testid="settings-modal"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="pcm-modal-header" data-testid="settings-modal-header">
          <h2 className="pcm-modal-title" data-testid="settings-modal-title">
            Settings
          </h2>
          <button
            type="button"
            className="pcm-modal-close"
            onClick={onClose}
            data-testid="settings-close-button"
          >
            ✕
          </button>
        </div>

        <div className="pcm-modal-section" data-testid="ai-provider-section">
          <h3 className="pcm-modal-subtitle" data-testid="ai-provider-title">
            AI Provider
          </h3>
          <label className="pcm-label" data-testid="provider-select-label">
            Active Provider
          </label>
          <select
            className="pcm-select"
            value={draftSettings.activeProvider}
            onChange={(event) =>
              onDraftSettingsChange({
                ...draftSettings,
                activeProvider: event.target.value,
              })
            }
            data-testid="provider-select"
          >
            {providers.map((provider) => (
              <option key={provider.id} value={provider.id}>
                {provider.label}
              </option>
            ))}
          </select>

          <div className="pcm-settings-label-row">
            <label className="pcm-label" data-testid="provider-key-label">
              {activeProviderLabel} API Key
            </label>
            {hint && (
              <button
                type="button"
                className="pcm-hint-trigger"
                onClick={() => setIsHintOpen((prev) => !prev)}
                data-testid="provider-hint-button"
              >
                How do I get this?
              </button>
            )}
          </div>

          {isHintOpen && hint && (
            <div className="pcm-hint-box" data-testid="provider-hint-box">
              {hint}
            </div>
          )}

          <input
            type="password"
            className="pcm-settings-input"
            value={draftSettings.keys?.[draftSettings.activeProvider] || ""}
            onChange={(event) =>
              onDraftSettingsChange({
                ...draftSettings,
                keys: {
                  ...draftSettings.keys,
                  [draftSettings.activeProvider]: event.target.value,
                },
              })
            }
            placeholder="Enter API key"
            data-testid="provider-key-input"
          />

          {draftSettings.activeProvider === "azure" ? (
            <>
              <label className="pcm-label" data-testid="azure-endpoint-label">
                Azure Endpoint URL
              </label>
              <input
                type="text"
                className="pcm-settings-input"
                value={draftSettings.azureEndpoint}
                onChange={(event) =>
                  onDraftSettingsChange({
                    ...draftSettings,
                    azureEndpoint: event.target.value,
                  })
                }
                placeholder="https://your-resource.openai.azure.com"
                data-testid="azure-endpoint-input"
              />
              <label className="pcm-label" data-testid="azure-deployment-label">
                Azure Deployment Name
              </label>
              <input
                type="text"
                className="pcm-settings-input"
                value={draftSettings.azureDeployment}
                onChange={(event) =>
                  onDraftSettingsChange({
                    ...draftSettings,
                    azureDeployment: event.target.value,
                  })
                }
                placeholder="deployment-name"
                data-testid="azure-deployment-input"
              />
            </>
          ) : null}
        </div>

        <div className="pcm-modal-section" data-testid="data-management-section">
          <h3 className="pcm-modal-subtitle" data-testid="data-management-title">
            Data Management
          </h3>
          <button
            type="button"
            className="pcm-button pcm-data-button"
            onClick={onExport}
            data-testid="export-button"
          >
            Export all projects as JSON backup
          </button>
          <button
            type="button"
            className="pcm-button pcm-data-button"
            onClick={() => fileInputRef.current?.click()}
            data-testid="import-button"
          >
            Import projects from JSON file
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json,application/json"
            onChange={onImport}
            className="pcm-file-input"
            data-testid="import-file-input"
          />
        </div>

        <button
          type="button"
          className="pcm-button pcm-button-primary pcm-settings-save"
          onClick={onSave}
          data-testid="save-settings-button"
        >
          Save Settings
        </button>
        <p className="pcm-modal-note" data-testid="settings-note">
          Your keys are stored locally and never sent to our servers.
        </p>
      </div>
    </div>
  );
};

export default SettingsModal;
